import React from 'react';
import { Datagrid, List, useListContext } from 'react-admin';
import { Box, Card, CardContent, Typography } from '@mui/material';
import {
	AccountNameField,
	AccountStateField,
	LastProcessField,
	ManagedCostField,
	ProjectsField,
	StorageField,
	SubscriptionField,
} from './cells';
import { AccountProjects } from './AccountProjects';
import { datagridColumnsSx } from './columnLayout';
import { PeriodRange } from './PeriodFilter';

/**
 * Prod compte 59 billing accounts portant un projet, staging 18 : tout tient sur
 * une page. Le plafond ne sert qu'à borner si ça grossit.
 */
const ACCOUNTS_PER_PAGE = 100;

/**
 * Le compteur vit DANS la List : il doit annoncer ce que le tableau montre. Lu
 * depuis le résumé, il ignorerait la recherche et afficherait « 37 » au-dessus
 * d'une seule ligne.
 */
const AccountsHeader: React.FC = () => {
	const { total } = useListContext();
	return (
		<CardContent sx={{ pb: 0 }}>
			<Typography variant="subtitle1" fontWeight={600}>
				Billing accounts, activité et conso managée ({total ?? 0})
			</Typography>
			<Typography variant="caption" color="text.secondary" display="block" sx={{ maxWidth: '60rem' }}>
				Tous les billing accounts portant au moins un projet. Un projet non managé pointe sur
				le datawarehouse du client : il ne coûte rien à Quanti, et c'est une information. Le
				dernier process ignore les runs de test de l'équipe, sinon un projet mort passerait
				pour vivant. Clique une ligne pour voir ses projets.
			</Typography>
		</CardContent>
	);
};

export const AccountsBlock: React.FC<{ range: PeriodRange }> = ({ range }) => (
	<Card sx={{ mb: 3 }}>
		<List
			resource="billing-account-dw-usage"
			// Deux listes sur la même page : sans ça, elles se disputent les mêmes
			// paramètres d'URL (tri, page, filtres) et se réinitialisent l'une l'autre.
			disableSyncWithLocation
			storeKey="billingAlerts.accounts"
			component={Box}
			title={false}
			actions={false}
			exporter={false}
			// Filtre permanent : la plage choisie s'applique aux montants, l'utilisateur
			// ne peut pas la contredire depuis la barre de recherche.
			filter={{ from: range.from, to: range.to }}
			perPage={ACCOUNTS_PER_PAGE}
			// ⚠️ Aucun `sort` volontairement, et ce n'est pas un oubli : le tri validé
			// est composite (`alert DESC, total_eur DESC`) et seul le back peut
			// l'exprimer. react-admin envoie alors son défaut `id ASC` ; `id` n'est pas
			// dans l'allow-list de tri d'api-core, qui retombe donc sur sa clause par
			// défaut — exactement celle qu'on veut. Poser un `sort` ici la remplacerait
			// entièrement et ferait perdre la remontée des comptes en alerte.
			empty={
				<Typography sx={{ p: 2 }} color="text.secondary">
					Aucun billing account.
				</Typography>
			}
		>
			<AccountsHeader />
			{/* Pas de `optimized` ici : le comparateur de PureDatagridRow exclut
			    `expand` de sa comparaison, et le panneau déplié resterait figé sur
			    l'ancienne plage, sans erreur. */}
			<Datagrid
				bulkActionButtons={false}
				rowClick="expand"
				expand={<AccountProjects range={range} />}
				// Colonnes figées : c'est ce qui permet aux lignes projet du panneau
				// déplié, rendues dans un second tableau, de tomber sous ces en-têtes.
				// Largeurs partagées dans columnLayout.ts, jamais redéclarées ici.
				sx={datagridColumnsSx}
			>
				<AccountNameField label="Billing account" sortBy="name" />
				<SubscriptionField label="Abonnement" sortBy="subscriptionStatus" />
				<ProjectsField label="Projets" sortBy="projectCount" textAlign="right" />
				<LastProcessField label="Dernier process" sortBy="lastProcessAt" />
				<StorageField label="Storage" sortBy="storageEur" textAlign="right" />
				<ManagedCostField label="Conso managée" sortBy="totalEur" textAlign="right" />
				<AccountStateField label="État" />
			</Datagrid>
		</List>
	</Card>
);
